import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Textarea } from '../components/ui/Textarea';
import { Plus, Filter, Download, Eye, Edit, Search, Ticket, Send, X, Save, CheckCircle2, Trash2, AlertCircle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
const initialTicketsData = Array.from({
  length: 50
}, (_, i) => ({
  id: String(i + 1),
  code: `TKT-2024/${String(i + 1).padStart(3, '0')}`,
  title: i % 2 === 0 ? 'Errore fatturazione' : 'Richiesta preventivo',
  client: `Azienda ${i} SRL`,
  priority: i % 3 === 0 ? 'Urgente' : 'Standard',
  status: i % 3 === 0 ? 'Aperto' : i % 3 === 1 ? 'In corso' : 'Chiuso',
  assignedTo: i % 4 === 0 ? 'Amministrazione' : i % 4 === 1 ? 'Alessio' : i % 4 === 2 ? 'Lorenzo' : 'Team Sviluppo',
  date: '2024-05-20',
  comments: [{
    user: 'Alessio',
    text: 'Preso in carico',
    date: '2024-05-20 10:00'
  }]
}));
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'title',
  label: 'Oggetto'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'priority',
  label: 'Priorità'
}, {
  key: 'assignedTo',
  label: 'Assegnato a'
}, {
  key: 'date',
  label: 'Data'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function TicketsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialTicketsData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['title', 'client', 'code']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      status: 'Aperto',
      priority: 'Standard'
    });
    setIsModalOpen(true);
  };
  const handleViewDetail = (item: any) => {
    setSelectedItem(item);
    setIsDetailOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteConfirmOpen(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setIsDeleteConfirmOpen(false);
      addNotification('success', 'Ticket eliminato', 'Il ticket è stato eliminato con successo.');
    }, 1500);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Ticket salvato', 'Il ticket è stato salvato con successo.');
    }, 2000);
  };
  const handleAddComment = () => {
    addNotification('success', 'Commento aggiunto', 'Il tuo commento è stato salvato.');
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-2729">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2730">
        <div data-id="element-2731">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2732">Ticket</h1>
          <p className="text-slate-500 mt-1" data-id="element-2733">
            Gestione richieste e segnalazioni.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2734">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2736" />} data-id="element-2735">
            Esporta
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2738" />} data-id="element-2737">
            Nuovo ticket
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-2739">
        <div className="p-4 flex flex-col gap-4" data-id="element-2740">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-2741">
            <div className="flex-1" data-id="element-2742">
              <Input placeholder="Cerca ticket..." startContent={<Search className="h-4 w-4" data-id="element-2744" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2743" />
            </div>
            <div className="flex gap-2" data-id="element-2745">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2747" />} data-id="element-2746">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2748">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="tickets" data-id="element-2749" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2750">
              <Select label="Priorità" value={filters.priority || ''} onChange={e => setFilter('priority', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Urgente',
            label: 'Urgente'
          }, {
            value: 'Standard',
            label: 'Standard'
          }]} data-id="element-2751" />
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Aperto',
            label: 'Aperto'
          }, {
            value: 'In corso',
            label: 'In corso'
          }, {
            value: 'Chiuso',
            label: 'Chiuso'
          }]} data-id="element-2752" />
              <Select label="Assegnato a" value={filters.assignedTo || ''} onChange={e => setFilter('assignedTo', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Alessio',
            label: 'Alessio'
          }, {
            value: 'Lorenzo',
            label: 'Lorenzo'
          }, {
            value: 'Amministrazione',
            label: 'Amministrazione'
          }, {
            value: 'Team Sviluppo',
            label: 'Team Sviluppo'
          }]} data-id="element-2753" />
              <div className="col-span-full flex justify-end" data-id="element-2754">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2756" />} data-id="element-2755">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-2757">
        <div className="overflow-x-auto" data-id="element-2758">
          <table className="w-full text-left border-collapse table-fixed" data-id="element-2759">
            <thead data-id="element-2760">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2761">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-left w-32' : ''}`} data-id="element-2762">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2763">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2764">
                  {visibleColumns.includes('code') && <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-2765">
                      {item.code}
                    </td>}
                  {visibleColumns.includes('title') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2766">
                      {item.title}
                    </td>}
                  {visibleColumns.includes('client') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2767">
                      {item.client}
                    </td>}
                  {visibleColumns.includes('priority') && <td className="px-6 py-4" data-id="element-2768">
                      <Badge variant={item.priority === 'Urgente' ? 'danger' : 'neutral'} data-id="element-2769">
                        {item.priority}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('assignedTo') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2770">
                      {item.assignedTo}
                    </td>}
                  {visibleColumns.includes('date') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2771">
                      {item.date}
                    </td>}
                  {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-2772">
                      <Badge variant={item.status === 'Aperto' ? 'warning' : item.status === 'In corso' ? 'info' : 'success'} data-id="element-2773">
                        {item.status}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-6 py-4" data-id="element-2774">
                      <div className="flex items-center gap-2" data-id="element-2775">
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleViewDetail(item)} title="Visualizza ticket" data-id="element-2776">
                          <Eye className="h-4 w-4 text-slate-500" data-id="element-2777" />
                        </Button>
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} title="Modifica ticket" data-id="element-2778">
                          <Edit className="h-4 w-4 text-seform-orange" data-id="element-2779" />
                        </Button>
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleDelete(item)} title="Elimina ticket" data-id="element-2780">
                          <Trash2 className="h-4 w-4 text-red-500" data-id="element-2781" />
                        </Button>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-2782">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2783" />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica ticket' : 'Nuovo ticket'} size="2xl" data-id="element-2784">
        <AnimatePresence mode="wait" data-id="element-2785">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2786">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2787">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2788" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2789">
                Ticket salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2790">
              <div className="space-y-8" data-id="element-2791">
                <section data-id="element-2792">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2793">
                    <Ticket className="h-4 w-4" data-id="element-2794" /> Dettagli ticket
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2795">
                    <Input label="Oggetto" value={formData.title || ''} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} required className="col-span-full" data-id="element-2796" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Errore',
                  label: 'Errore software'
                }, {
                  value: 'Richiesta',
                  label: 'Richiesta'
                }, {
                  value: 'Assistenza',
                  label: 'Assistenza tecnica'
                }, {
                  value: 'Amministrativo',
                  label: 'Amministrativo'
                }]} required data-id="element-2797" />
                    <Select label="Cliente (opzionale)" value={formData.client || ''} onChange={e => setFormData({
                  ...formData,
                  client: e.target.value
                })} options={[{
                  value: '',
                  label: 'Nessuno'
                }, {
                  value: 'Azienda 1 SRL',
                  label: 'Azienda 1 SRL'
                }, {
                  value: 'Azienda 2 SRL',
                  label: 'Azienda 2 SRL'
                }]} data-id="element-2798" />
                    <div className="col-span-full" data-id="element-2799">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-2800" />
                    </div>
                    <Select label="Priorità" value={formData.priority || 'Standard'} onChange={e => setFormData({
                  ...formData,
                  priority: e.target.value
                })} options={[{
                  value: 'Urgente',
                  label: 'Urgente'
                }, {
                  value: 'Standard',
                  label: 'Standard'
                }]} required data-id="element-2801" />
                    <Select label="Stato" value={formData.status || 'Aperto'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Aperto',
                  label: 'Aperto'
                }, {
                  value: 'In corso',
                  label: 'In corso'
                }, {
                  value: 'Chiuso',
                  label: 'Chiuso'
                }]} required data-id="element-2802" />
                    <Input label="Scadenza" type="date" value={formData.deadline || ''} onChange={e => setFormData({
                  ...formData,
                  deadline: e.target.value
                })} data-id="element-2803" />
                    <Select label="Assegnato a" value={formData.assignedTo || ''} onChange={e => setFormData({
                  ...formData,
                  assignedTo: e.target.value
                })} options={[{
                  value: 'Alessio',
                  label: 'Alessio'
                }, {
                  value: 'Lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'Amministrazione',
                  label: 'Amministrazione'
                }, {
                  value: 'Team Sviluppo',
                  label: 'Team Sviluppo'
                }]} data-id="element-2804" />
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2805">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-2806">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2808" />} data-id="element-2807">
                    Salva ticket
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} title={<div className="flex items-center gap-3" data-id="element-2810">
            <span data-id="element-2811">Ticket {selectedItem?.code}</span>
            {selectedItem && <Badge variant={selectedItem.status === 'Aperto' ? 'warning' : selectedItem.status === 'In corso' ? 'info' : 'success'} data-id="element-2812">
                {selectedItem.status}
              </Badge>}
          </div>} size="lg" data-id="element-2809">
        <div className="space-y-6" data-id="element-2813">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-2814">
            <div className="flex justify-between items-start mb-2" data-id="element-2815">
              <h3 className="font-bold text-seform-navy text-lg" data-id="element-2816">
                {selectedItem?.title}
              </h3>
              <Badge variant={selectedItem?.priority === 'Urgente' ? 'danger' : 'neutral'} data-id="element-2817">
                {selectedItem?.priority}
              </Badge>
            </div>
            <p className="text-sm text-slate-600 mb-4" data-id="element-2818">
              Cliente: {selectedItem?.client}
            </p>
            <div className="flex gap-4 text-xs text-slate-500 border-t border-slate-200 pt-2" data-id="element-2819">
              <span data-id="element-2820">Creato il: {selectedItem?.date}</span>
              <span data-id="element-2821">Assegnato a: {selectedItem?.assignedTo}</span>
            </div>
          </div>

          <div className="space-y-4 max-h-[300px] overflow-y-auto custom-scrollbar p-2" data-id="element-2822">
            {selectedItem?.comments.map((comment: any, idx: number) => <div key={idx} className="flex gap-3" data-id="element-2823">
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 flex-shrink-0" data-id="element-2824">
                  {comment.user.charAt(0)}
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-3 flex-1 shadow-sm" data-id="element-2825">
                  <div className="flex justify-between items-center mb-1" data-id="element-2826">
                    <span className="font-bold text-xs text-seform-navy" data-id="element-2827">
                      {comment.user}
                    </span>
                    <span className="text-[10px] text-slate-400" data-id="element-2828">
                      {comment.date}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700" data-id="element-2829">{comment.text}</p>
                </div>
              </div>)}
          </div>

          <div className="flex gap-2 items-start pt-4 border-t border-slate-100" data-id="element-2830">
            <div className="flex-1" data-id="element-2831">
              <Textarea rows={2} data-id="element-2832" />
            </div>
            <Button onClick={handleAddComment} startContent={<Send className="h-4 w-4" data-id="element-2834" />} data-id="element-2833">
              Invia
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Conferma eliminazione" size="sm" data-id="element-2835">
        <AnimatePresence mode="wait" data-id="element-2836">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-2837">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2838">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-2839" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2840">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2841">
              <div className="space-y-6" data-id="element-2842">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-2843">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-2844" />
                  <p className="text-sm text-red-700" data-id="element-2845">
                    Attenzione: questa azione è irreversibile. Il ticket verrà
                    eliminato definitivamente.
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-2846">
                  <Button variant="flat" color="default" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-2847">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} data-id="element-2848">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}